<script setup>
import { ref } from 'vue'
import { api } from '../composables/useApi.js'
import { useButtonEvent } from '../composables/useButtonEvent.js'
import {
    formatSecondsToFullTime,
    formatTimestamp,
    formatUser,
} from '../composables/useFormatting.js'

const data = ref([])
const view = ref('recent')

const loadData = () => {
    if (view.value === 'topusers') {
        api.get('/api/users/leaderboard/')
            .then((response) => {
                data.value = response.data
            })
            .catch((error) => {
                console.error('Error fetching data:', error)
            })
    } else {
        const params =
            view.value === 'highscores'
                ? {
                      ordering: '-elapsed_seconds',
                      page_size: 10,
                      owner__isnull: false,
                  }
                : { ordering: '-timestamp', page_size: 10 }

        api.get('/api/buttonpresses/', { params })
            .then((response) => {
                data.value = response.data.results
            })
            .catch((error) => {
                console.error('Error fetching data:', error)
            })
    }
}

useButtonEvent(loadData)

// Load data on mount (useButtonEvent handles the event listener)
loadData()
</script>

<template>
    <div class="leaderboard__view-selector">
        <div class="leaderboard__view-selector-group">
            <input
                type="radio"
                id="view-recent"
                value="recent"
                v-model="view"
                @change="loadData"
            />
            <label for="view-recent">Recent</label>

            <input
                type="radio"
                id="view-highscores"
                value="highscores"
                v-model="view"
                @change="loadData"
            />
            <label for="view-highscores">Highscores</label>

            <input
                type="radio"
                id="view-topusers"
                value="topusers"
                v-model="view"
                @change="loadData"
            />
            <label for="view-topusers">Top Users</label>
        </div>
    </div>

    <div class="leaderboard">
        <!-- Presses table (Recent / Highscores) -->
        <table
            v-if="view !== 'topusers'"
            class="leaderboard__table"
            aria-label="Button presses leaderboard"
        >
            <thead>
                <tr>
                    <th scope="col">Time claimed (seconds)</th>
                    <th scope="col">Claimed at</th>
                    <th scope="col">Tier</th>
                    <th scope="col">User</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="entry in data" :key="entry.id">
                    <td>
                        <a
                            :href="
                                entry.owner
                                    ? `/user/${entry.owner}/press/${entry.id}/`
                                    : `/press/${entry.id}/`
                            "
                            class="leaderboard__link"
                        >
                            {{ formatSecondsToFullTime(entry.elapsed_seconds) }}
                        </a>
                    </td>
                    <td>{{ formatTimestamp(entry.timestamp) }}</td>
                    <td>{{ entry.tier_label }}</td>
                    <td>
                        <a
                            v-if="entry.owner"
                            :href="`/user/${entry.owner}/`"
                            class="leaderboard__link"
                        >
                            {{ entry.owner }}
                        </a>
                        <span v-else>{{ formatUser(entry.owner) }}</span>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Users table (Top Users) -->
        <table
            v-else
            class="leaderboard__table"
            aria-label="Top users leaderboard"
        >
            <thead>
                <tr>
                    <th scope="col">Rank</th>
                    <th scope="col">User</th>
                    <th scope="col">Total Time Claimed</th>
                    <th scope="col">Presses</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(entry, index) in data" :key="entry.username">
                    <td>{{ index + 1 }}</td>
                    <td>
                        <a
                            :href="`/user/${entry.username}/`"
                            class="leaderboard__link"
                        >
                            {{ entry.username }}
                        </a>
                    </td>
                    <td>
                        {{ formatSecondsToFullTime(entry.total_time_claimed) }}
                    </td>
                    <td>{{ entry.press_count }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
